<template>
  <router-link 
    class="nav-link sidenav-item" 
    :to="to" 
    v-bind="$attrs"
    :class="{ 
      'active': isActive,
      'exact-match': activityLevel === 'exact',
      'partial-match': activityLevel === 'partial'
    }"
    @mouseenter="handleHover(true)"
    @mouseleave="handleHover(false)"
    :aria-current="isActive ? 'page' : null"
  >
    <!-- Indicador lateral principal -->
    <div class="nav-item-indicator" :class="{ 'active': isActive, [activityLevel]: true }"></div>
    
    <!-- Contenedor de iconos mejorado -->
    <div 
      class="sidenav-icon-container icon"
      :class="[
        this.$store.state.isRTL ? 'ms-2' : 'me-2',
        { 
          'active': isActive, 
          'hovered': isHovered,
          'exact-match': activityLevel === 'exact',
          'partial-match': activityLevel === 'partial'
        }
      ]"
    >
      <slot name="icon"></slot>
      
      <!-- Indicador de punto activo dentro del icono -->
      <div 
        v-if="isActive" 
        class="active-dot"
        :class="activityLevel"
      ></div>
      
      <!-- Halo de actividad -->
      <div 
        v-if="isActive" 
        class="activity-halo"
        :class="activityLevel"
      ></div>
    </div>
    
    <!-- Texto de navegación -->
    <span
      class="nav-link-text"
      :class="[
        this.$store.state.isRTL ? 'me-1' : 'ms-1',
        { 
          'active': isActive,
          'exact-match': activityLevel === 'exact',
          'partial-match': activityLevel === 'partial'
        }
      ]"
    >
      {{ navText }}
    </span>
    
    
    <!-- Efecto de brillo de fondo -->
    <div class="background-glow" :class="{ 'active': isActive }"></div>
  </router-link>
</template>
<script>
export default {
  name: "sidenav-collapse",
  props: {
    to: {
      type: [Object, String],
      required: true,
    },
    navText: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isExpanded: false,
      isHovered: false,
    };
  },
  computed: {
    isActive() {
      // Verificar si la ruta actual coincide con la ruta del elemento
      if (typeof this.to === 'string') {
        // Comparar path exacto y también rutas base
        return this.$route.path === this.to || 
               (this.to !== '/' && this.$route.path.startsWith(this.to));
      }
      if (this.to.name) {
        return this.$route.name === this.to.name;
      }
      if (this.to.path) {
        return this.$route.path === this.to.path || 
               (this.to.path !== '/' && this.$route.path.startsWith(this.to.path));
      }
      return false;
    },
    
    // Nivel de actividad para diferentes intensidades visuales
    activityLevel() {
      if (!this.isActive) return 'inactive';
      
      // Verificar si es una coincidencia exacta
      const isExactMatch = (typeof this.to === 'string' && this.$route.path === this.to) ||
                          (this.to.name && this.$route.name === this.to.name) ||
                          (this.to.path && this.$route.path === this.to.path);
      
      return isExactMatch ? 'exact' : 'partial';
    }
  },
  methods: {
    handleHover(hovered) {
      this.isHovered = hovered;
    }
  }
};
</script>

<style scoped>
/* Estilo principal del elemento de navegación con glassmorphin mejorado */
.sidenav-item {
  position: relative;
  padding: 0.75rem 1rem;
  margin: 0.125rem 0.5rem;
  border-radius: 0.75rem;
  text-decoration: none;
  color: var(--dm-text-secondary, #67748e);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
  background: linear-gradient(135deg, var(--dm-bg-accent-alpha, transparent) 0%, var(--dm-bg-accent-alpha-2, transparent) 100%);
  border: 1px solid var(--dm-border-accent, transparent);
  backdrop-filter: blur(15px);
  box-shadow: 0 2px 8px var(--dm-shadow-light, transparent);
}

/* Hover effects con glassmorphin mejorado */
.sidenav-item:hover {
  background: linear-gradient(135deg, var(--dm-bg-accent-hover, rgba(23, 173, 55, 0.08)) 0%, var(--dm-bg-accent-hover-2, rgba(152, 236, 45, 0.08)) 100%);
  border-color: var(--dm-border-accent-hover, rgba(23, 173, 55, 0.15));
  color: var(--dm-accent-primary, #17ad37);
  transform: translateX(2px);
  box-shadow: 0 4px 15px var(--dm-shadow-accent, rgba(23, 173, 55, 0.15));
  backdrop-filter: blur(20px);
}

/* Estados activos mejorados */
.sidenav-item.active {
  background: linear-gradient(135deg, #17ad37 0%, #98ec2d 100%);
  color: white;
  box-shadow: 0 8px 25px rgba(23, 173, 55, 0.3);
  transform: translateX(5px);
  position: relative;
  z-index: 2;
}

.sidenav-item.exact-match {
  box-shadow: 0 10px 30px rgba(23, 173, 55, 0.4), 
              0 0 0 1px rgba(255, 255, 255, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.2);
  animation: activeItemGlow 3s ease-in-out infinite;
}

.sidenav-item.partial-match {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.8) 0%, rgba(152, 236, 45, 0.8) 100%);
  box-shadow: 0 6px 20px rgba(23, 173, 55, 0.25);
}

.sidenav-item.active:hover {
  transform: translateX(6px) scale(1.02);
}

@keyframes activeItemGlow {
  0%, 100% {
    box-shadow: 0 10px 30px rgba(23, 173, 55, 0.4), 
                0 0 0 1px rgba(255, 255, 255, 0.1),
                inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }
  50% {
    box-shadow: 0 15px 40px rgba(23, 173, 55, 0.6), 
                0 0 0 1px rgba(255, 255, 255, 0.2),
                inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }
}

/* Container de iconos con glassmorphin mejorado */
.sidenav-icon-container {
  position: relative;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  background: linear-gradient(135deg, var(--dm-bg-icon, rgba(255, 255, 255, 0.9)) 0%, var(--dm-bg-icon-2, rgba(245, 245, 245, 0.9)) 100%);
  box-shadow: 0 2px 8px var(--dm-shadow-icon, rgba(0, 0, 0, 0.08));
  backdrop-filter: blur(15px);
  border: 1px solid var(--dm-border-icon, rgba(255, 255, 255, 0.3));
  flex-shrink: 0;
}

/* Estados del contenedor de iconos mejorados */
.sidenav-icon-container.hovered {
  transform: scale(1.1) rotate(2deg);
  box-shadow: 0 4px 15px var(--dm-shadow-accent, rgba(23, 173, 55, 0.2));
  backdrop-filter: blur(20px);
  border-color: var(--dm-border-icon-hover, rgba(23, 173, 55, 0.2));
}

.sidenav-icon-container.active {
  background: linear-gradient(135deg, var(--dm-bg-icon-active, rgba(255, 255, 255, 1)) 0%, var(--dm-bg-icon-active-2, rgba(248, 250, 252, 1)) 100%);
  transform: scale(1.1);
  box-shadow: 0 8px 25px var(--dm-shadow-icon-active, rgba(255, 255, 255, 0.4));
  border: 2px solid var(--dm-border-icon-active, rgba(255, 255, 255, 0.5));
  backdrop-filter: blur(25px);
}

.sidenav-icon-container.exact-match {
  transform: scale(1.15);
  animation: iconPulse 2s ease-in-out infinite;
}

.sidenav-icon-container.partial-match {
  transform: scale(1.08);
  opacity: 0.95;
}

@keyframes iconPulse {
  0%, 100% {
    transform: scale(1.15);
    box-shadow: 0 8px 25px rgba(255, 255, 255, 0.4);
  }
  50% {
    transform: scale(1.2);
    box-shadow: 0 12px 35px rgba(255, 255, 255, 0.6);
  }
}

/* Iconos SVG */
.sidenav-icon-container :deep(svg) {
  width: 1rem;
  height: 1rem;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
  color: #6b7280;
  flex-shrink: 0;
}

.sidenav-item:hover .sidenav-icon-container :deep(svg) {
  color: #17ad37;
  transform: scale(1.1);
}

.sidenav-item.active .sidenav-icon-container :deep(svg) {
  color: #17ad37;
  transform: scale(1.15);
}

/* Texto de navegación */
.nav-link-text {
  font-weight: 600;
  font-size: 0.8125rem;
  letter-spacing: 0.025em;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
}

.nav-link-text.active {
  color: white;
  font-weight: 700;
}

/* Indicador lateral mejorado */
.nav-item-indicator {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 0;
  background: linear-gradient(to bottom, #17ad37, #98ec2d);
  border-radius: 0 3px 3px 0;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 3;
}

.nav-item-indicator.active {
  height: 70%;
  width: 5px;
  box-shadow: 0 0 15px rgba(23, 173, 55, 0.5);
}

.nav-item-indicator.exact {
  height: 80%;
  width: 6px;
  animation: indicatorPulse 2s ease-in-out infinite;
}

.nav-item-indicator.partial {
  height: 60%;
  width: 4px;
  opacity: 0.8;
}

@keyframes indicatorPulse {
  0%, 100% {
    box-shadow: 0 0 15px rgba(23, 173, 55, 0.5);
    width: 6px;
  }
  50% {
    box-shadow: 0 0 25px rgba(23, 173, 55, 0.8);
    width: 7px;
  }
}

/* Efectos de glassmorphism */
.sidenav-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  border-radius: 0.75rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.sidenav-item:hover::before {
  opacity: 1;
}

/* Nuevos elementos visuales para estados activos */

/* Punto activo dentro del icono */
.active-dot {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 8px;
  height: 8px;
  background: linear-gradient(135deg, #17ad37 0%, #98ec2d 100%);
  border: 2px solid white;
  border-radius: 50%;
  z-index: 10;
  animation: dotPulse 2s ease-in-out infinite;
}

.active-dot.exact {
  width: 10px;
  height: 10px;
  background: #fff;
  box-shadow: 0 0 0 2px #17ad37, 0 2px 8px rgba(23, 173, 55, 0.4);
}

.active-dot.partial {
  width: 6px;
  height: 6px;
  opacity: 0.8;
}

@keyframes dotPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
}

/* Halo de actividad */
.activity-halo {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  background: radial-gradient(circle, rgba(23, 173, 55, 0.2) 0%, transparent 70%);
  border-radius: 50%;
  z-index: 0;
  animation: haloGlow 3s ease-in-out infinite;
}

.activity-halo.exact {
  animation: haloGlowIntense 2s ease-in-out infinite;
}

@keyframes haloGlow {
  0%, 100% { opacity: 0.3; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.1); }
}

@keyframes haloGlowIntense {
  0%, 100% { opacity: 0.4; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.2); }
}


/* Efecto de brillo de fondo */
.background-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  border-radius: 0.75rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 1;
}

.background-glow.active {
  opacity: 1;
  animation: backgroundShimmer 3s ease-in-out infinite;
}

@keyframes backgroundShimmer {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}


/* Estados específicos para modo oscuro */
body.dark-mode-active .sidenav-item {
  background: linear-gradient(135deg, var(--dm-bg-accent-alpha-dark, rgba(23, 173, 55, 0.05)) 0%, var(--dm-bg-accent-alpha-2-dark, rgba(152, 236, 45, 0.05)) 100%);
  border-color: var(--dm-border-accent-dark, rgba(23, 173, 55, 0.12));
  backdrop-filter: blur(20px);
  color: var(--dm-text-secondary-dark, #d1d5db);
}

body.dark-mode-active .sidenav-item:hover {
  background: linear-gradient(135deg, var(--dm-bg-accent-hover-dark, rgba(23, 173, 55, 0.12)) 0%, var(--dm-bg-accent-hover-2-dark, rgba(152, 236, 45, 0.12)) 100%);
  border-color: var(--dm-border-accent-hover-dark, rgba(23, 173, 55, 0.25));
  backdrop-filter: blur(25px);
}

body.dark-mode-active .sidenav-icon-container {
  background: linear-gradient(135deg, var(--dm-bg-icon-dark, rgba(55, 65, 81, 0.9)) 0%, var(--dm-bg-icon-2-dark, rgba(75, 85, 99, 0.9)) 100%);
  border-color: var(--dm-border-icon-dark, rgba(255, 255, 255, 0.1));
  backdrop-filter: blur(20px);
}

body.dark-mode-active .sidenav-icon-container.hovered {
  border-color: var(--dm-border-icon-hover-dark, rgba(23, 173, 55, 0.3));
  backdrop-filter: blur(25px);
}

body.dark-mode-active .sidenav-icon-container.active {
  background: linear-gradient(135deg, var(--dm-bg-icon-active-dark, rgba(75, 85, 99, 1)) 0%, var(--dm-bg-icon-active-2-dark, rgba(55, 65, 81, 1)) 100%);
  border-color: var(--dm-border-icon-active-dark, rgba(255, 255, 255, 0.2));
  backdrop-filter: blur(30px);
}

/* Responsive design mejorado */
@media (max-width: 1200px) {
  .sidenav-item {
    padding: 0.5rem 0.75rem;
    margin: 0.125rem 0.375rem;
  }
  
  .sidenav-icon-container {
    width: 1.875rem;
    height: 1.875rem;
  }
  
  .sidenav-icon-container :deep(svg) {
    width: 0.9375rem;
    height: 0.9375rem;
  }
  
  .nav-link-text {
    font-size: 0.75rem;
  }
  
  
  .active-dot {
    width: 6px;
    height: 6px;
  }
  
  .active-dot.exact {
    width: 8px;
    height: 8px;
  }
}

@media (max-width: 992px) {
  .sidenav-item {
    padding: 0.4375rem 0.6875rem;
    margin: 0.0625rem 0.3125rem;
  }
  
  .sidenav-icon-container {
    width: 1.75rem;
    height: 1.75rem;
  }
  
  .sidenav-icon-container :deep(svg) {
    width: 0.875rem;
    height: 0.875rem;
  }
  
  .nav-link-text {
    font-size: 0.6875rem;
  }
  
  
  .active-dot {
    width: 5px;
    height: 5px;
  }
  
  .active-dot.exact {
    width: 7px;
    height: 7px;
  }
}

@media (max-width: 768px) {
  .sidenav-item {
    padding: 0.375rem 0.625rem;
    margin: 0.0625rem 0.25rem;
  }
  
  .sidenav-icon-container {
    width: 1.625rem;
    height: 1.625rem;
  }
  
  .sidenav-icon-container :deep(svg) {
    width: 0.8125rem;
    height: 0.8125rem;
  }
  
  .nav-link-text {
    font-size: 0.625rem;
  }
  
  
  .active-dot {
    width: 4px;
    height: 4px;
  }
  
  .active-dot.exact {
    width: 6px;
    height: 6px;
  }
  
  /* Simplificar animaciones en móviles para mejor performance */
  .sidenav-item.exact-match {
    animation: none;
  }
  
  .activity-halo {
    display: none;
  }
}




</style>
